import React, { useRef, useState } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import Button from "@src/components/core-button"

const DIRECTIONS = {
  prev: "prev",
  next: "next",
}

const ContentContainer = styled.div`
  & .prev {
    left: calc(50% - 15px);
    top: -15px;
    transform: rotate(90deg);
  }
  & .next {
    left: calc(50% - 15px);
    top: calc(100% - 30px);
    transform: rotate(90deg);
  }
  @media (max-width: 550px) {
    & .prev {
      left: calc(50% - 15px);
      top: -15px;
      transform: rotate(90deg);
    }
    & .next {
      left: calc(50% - 15px);
      top: calc(100% - 45px);
      transform: rotate(90deg);
    }
  }

  position: relative;

  & > .carousel-content {
    scroll-behavior: smooth;
    scroll-snap-type: y mandatory;
    position: relative;
    display: flex;
    flex-direction: column;
    overflow-y: scroll;
    max-height: 782px;
    @media (max-width: 550px) {
      max-height: 420px;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    & > * {
      flex: 0 0 auto;
      /* height: 100%; */
      scroll-snap-align: start; /* Snap the item into view */
      margin: 16px 0; /* Adjust vertical spacing */
    }
  }

  & > button {
    cursor: pointer;
    position: absolute;
    left: 50%; /* Center horizontally */
    transform: translateX(-50%);
    z-index: ${props => props.theme.layers.floatingUI};
    transition: opacity 0.3s ease-in-out;
    opacity: 1;

    &:not(.show) {
      opacity: ${props => (props.$alwaysShowButtons ? "1" : "0")};
      cursor: ${props => (props.$alwaysShowButtons ? "disabled" : "default")};
    }
  }
`

const ContentCarousel = ({
  children,
  className,
  buttonType = "circle",
  buttonColor,
  mobileSizeOnly = false,
  icons = {
    left: "chevron-up", // Use upward chevron for vertical layout
    right: "chevron-down", // Use downward chevron for vertical layout
  },
  alwaysShowButtons = false,
  variant,
}) => {
  const [showButtons, setShowButtons] = useState({
    left: false,
    right: true,
  })
  const ref = useRef(null)

  const refreshButtons = (clientHeight, scrollHeight, newScrollTop) => {
    const shouldShowButtons = clientHeight !== scrollHeight
    setShowButtons({
      left: shouldShowButtons && newScrollTop > 10,
      right:
        shouldShowButtons && newScrollTop + clientHeight < scrollHeight - 5,
    })
  }

  const scrollCarousel = (direction = DIRECTIONS.next) => {
    if (ref.current) {
      const offsetHeight = ref.current.offsetHeight
      const currentScrollTop = ref.current.scrollTop
      const scrollHeight = ref.current.scrollHeight
      const clientHeight = ref.current.clientHeight

      // Calculate new scroll position
      const newScrollTop = Math.min(
        Math.max(
          currentScrollTop +
            (direction === DIRECTIONS.next ? offsetHeight : -offsetHeight),
          0,
        ),
        scrollHeight - clientHeight,
      )

      // Update the scroll position
      ref.current.scrollTop = newScrollTop

      // Pass the calculated `newScrollTop` to `refreshButtons`
      refreshButtons(clientHeight, scrollHeight, newScrollTop)
    }
  }

  return (
    <>
      <ContentContainer
        className={`carousel-container ${className ? className : ""}`}
        $alwaysShowButtons={alwaysShowButtons}
        $variant={variant}
      >
        <div ref={ref} className="carousel-content">
          {children}
        </div>
        <Button
          className={`prev${showButtons.left ? " show" : ""}`}
          disabled={!showButtons.left}
          variant={buttonType}
          color={buttonColor}
          mobileSizeOnly={mobileSizeOnly}
          icon={icons.left}
          onClick={() => scrollCarousel(DIRECTIONS.prev)}
          aria-label="previous"
        />
        <Button
          className={`next${showButtons.right ? " show" : ""}`}
          disabled={!showButtons.right}
          variant={buttonType}
          color={buttonColor}
          mobileSizeOnly={mobileSizeOnly}
          icon={icons.right}
          onClick={() => scrollCarousel(DIRECTIONS.next)}
          aria-label="next"
        />
      </ContentContainer>
    </>
  )
}

ContentCarousel.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  active: PropTypes.bool,
  buttonType: PropTypes.oneOf(["circle", "icon", "none"]),
  buttonColor: PropTypes.oneOf([
    "yellow",
    "blue",
    "yellow-black",
    "outline",
    "outline-black",
    "gray",
    "purple",
    "red",
    "white-gray",
  ]),
  icons: PropTypes.shape({ left: PropTypes.string, right: PropTypes.string }),
  mobileSizeOnly: PropTypes.bool,
  alwaysShowButtons: PropTypes.bool,
  variant: PropTypes.string,
}

export default ContentCarousel
